import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Login from './pages/Login';
// import NavigationBarNew from './components/navigation/NavigationBarNew';
import { useDispatch, useSelector } from 'react-redux';
import { cargarSesion } from './redux/slices/sessionSlice';
import RegisterForm from './pages/RegisterForm';
import { ThemeProvider } from '@emotion/react';
import theme from './theme/theme';
import Loading from './components/objects/Loading';
import Properties from './pages/Properties';
import useStyles from './theme/useStyles';
import PropertyForm from './pages/PropertyForm';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import PropertyEditForm from './pages/PropertyEditForm';
import MapaPage from './pages/MapaPage';
import { Box } from '@mui/material';

// Importar páginas del workflow
import DashboardInscripcion from './pages/workflow/DashboardInscripcion';
import RegistroEntidad from './pages/workflow/RegistroEntidad';
import CargaDocumentos from './pages/workflow/CargaDocumentos';
import RevisionAdministrativa from './pages/workflow/RevisionAdministrativa';
import HistorialWorkflow from './pages/workflow/HistorialWorkflow';
import DashboardAdmin from './pages/admin/DashboardAdmin';

// Importar grid de entidades
import GridEntidadesCRUD from './pages/GridEntidadesCRUD';
import SinConexion from './pages/SinConexion';
// import LandingPage from './pages/LandingPage';
import ProyectoPresentacion from './pages/ProyectoPresentacion';
import BlockchainIntegration from './pages/BlockchainIntegration';
import DashboardAnalitico from './pages/DashboardAnalitico';
import ExecutiveSummary from './pages/ExecutiveSummary.tsx';
import VentaFase1Tracker from './pages/VentaFase1Tracker.tsx';
import CommitteeArchitecture from './pages/CommitteeArchitecture.tsx';
import NFTCooperativasPresentation from './pages/NFTCooperativasPresentation.tsx';
import AnalisisComisionesMUI from './pages/AnalisisComisiones.tsx';
// import LandingPageAlianzaSocial from './pages/LandingPageAlianzaSocial.jsx';
import NavigationBarCapellanes from './components/navigation/NavigationBarCapellanes.js';
import MovimientoPage from './pages/MovimientoPage.jsx';
import DesbloquearNivelesPage from './pages/DesbloquearNivelesPage.jsx';
import Usuarios from './pages/admin/Usuarios.jsx';
import EditarUsuario from './pages/admin/EditarUsuario.jsx';
import TarjetaPresentacion from './pages/TarjetaPresentacion.jsx';
import PerfilesOnboarding from './pages/PerfilesOnboarding.jsx';
import LandingPageCapellanes from './pages/LandingPageCapellanes.jsx';
import Inscripcion from './pages/Inscripcion.jsx';
import ValidacionCarnet from './pages/ValidacionCarnet.jsx';

// Componente wrapper que añade padding según la ruta
const ContentWrapper = ({ children, classes, isLoggedIn }) => {
    const location = useLocation();
    
    // Rutas que NO necesitan padding-top (páginas con hero fullscreen)
    const fullscreenRoutes = ['/', '/home', '/movimiento'];
    const needsPadding = !fullscreenRoutes.includes(location.pathname)
      && !location.pathname.startsWith('/v/');
    
    return (
        <Box sx={{ 
            paddingTop: needsPadding ? '30px' : '0',
            minHeight: '100vh',
            marginTop: needsPadding ? '-30px' : '0', // Compensa el padding para que el fondo empiece desde arriba
        }}>
            <div className={isLoggedIn ? classes.content : classes.contentFullScreen} style={{
                paddingTop: needsPadding ? '30px' : '0' // Añade padding interno para que el contenido no quede bajo el navbar
            }}>
                {children}
            </div>
        </Box>
    );
};

// Componente para mostrar/ocultar NavigationBar según la ruta
const ConditionalNavbar = () => {
    const location = useLocation();
    const hideNavbarRoutes = ['/login', '/register'];
    const [lang, setLang] = React.useState(
        localStorage.getItem('preferredLanguage') || 'es'
    );

    React.useEffect(() => {
        const handler = (e) => {
            if (e.key === 'preferredLanguage' && e.newValue) setLang(e.newValue);
        };
        window.addEventListener('storage', handler);
        return () => window.removeEventListener('storage', handler);
    }, []);

    if (hideNavbarRoutes.includes(location.pathname) || location.pathname.startsWith('/v/')) {
        return null;
    }

    return <NavigationBarCapellanes key={lang} />;
};

const App = () => {
    const dispatch = useDispatch();
    const { isLoggedIn, user } = useSelector((state) => state.session);
    const classes = useStyles();

    useEffect(() => {
        dispatch(cargarSesion());
    }, [dispatch]);

    // Componente para proteger rutas de administrador
    const ProtectedAdminRoute = ({ children }) => {
        const esAdmin = user?.rol === 'Admin' || user?.rol === 'Auditor';
        
        if (!esAdmin) {
            return <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                    <div className="text-red-500 text-6xl mb-4">🚫</div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Acceso Denegado</h2>
                    <p className="text-gray-600 mb-4">No tiene permisos para acceder a esta página</p>
                </div>
            </div>;
        }
        
        return children;
    };

    // Componente para proteger rutas que requieren login
    const ProtectedRoute = ({ children }) => {
        if (!isLoggedIn) {
            return <Login />;
        }
        return children;
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
            <ThemeProvider theme={theme}>
                <Router>
                    {/* <NavigationBarCapellanes /> */}
                    <ConditionalNavbar />
                    <ContentWrapper classes={classes} isLoggedIn={isLoggedIn}>
                        <Routes>
                            {/* Rutas existentes */}
                            <Route path="/properties" element={
                                <ProtectedRoute>
                                    <Properties />
                                </ProtectedRoute>
                            } />
                            <Route path="/login" element={<Login />} />
                            <Route path="/register" element={<RegisterForm />} />
                            <Route path="/property" element={
                                <ProtectedRoute>
                                    <PropertyForm />
                                </ProtectedRoute>
                            } />
                            <Route path="/propertyEdit/:id" element={
                                <ProtectedRoute>
                                    <PropertyEditForm />
                                </ProtectedRoute>
                            } />
                            
                            {/* Ruta para gestión de entidades */}
                            <Route path="/entidades" element={
                                <ProtectedRoute>
                                    <GridEntidadesCRUD />
                                </ProtectedRoute>
                            } />
                            
                            {/* Ruta del mapa - ahora protegida */}
                            <Route path="/mapa" element={
                                <ProtectedRoute>
                                    <MapaPage />
                                </ProtectedRoute>
                            } />

                            {/* Validación pública de carnets QR — SIN ProtectedRoute */}
                            <Route path="/v/:codigo" element={<ValidacionCarnet />} />

                            {/* Inscripción pública — SIN ProtectedRoute — debe ir ANTES del wildcard /:entidadId */}
                            <Route path="/inscripcion" element={<Inscripcion />} />

                            {/* Rutas del workflow de inscripción */}
                            <Route path="/inscripcion/:entidadId" element={
                                <ProtectedRoute>
                                    <DashboardInscripcion />
                                </ProtectedRoute>
                            } />
                            
                            <Route path="/inscripcion/:entidadId/registro" element={
                                <ProtectedRoute>
                                    <RegistroEntidad />
                                </ProtectedRoute>
                            } />
                            
                            <Route path="/inscripcion/:entidadId/documentos" element={
                                <ProtectedRoute>
                                    <CargaDocumentos />
                                </ProtectedRoute>
                            } />
                            
                            <Route path="/inscripcion/:entidadId/revision" element={
                                <ProtectedRoute>
                                    <ProtectedAdminRoute>
                                        <RevisionAdministrativa />
                                    </ProtectedAdminRoute>
                                </ProtectedRoute>
                            } />
                            
                            <Route path="/inscripcion/:entidadId/historial" element={
                                <ProtectedRoute>
                                    <HistorialWorkflow />
                                </ProtectedRoute>
                            } />
                            
                            {/* Rutas de administración */}
                            <Route path="/admin/dashboard" element={
                                <ProtectedRoute>
                                    <ProtectedAdminRoute>
                                        <DashboardAdmin />
                                    </ProtectedAdminRoute>
                                </ProtectedRoute>
                            } />
                            
                            {/* Ruta sin conexión */}
                            <Route path="/sin-conexion" element={
                                <ProtectedRoute>
                                    <ProtectedAdminRoute>
                                        <SinConexion />
                                    </ProtectedAdminRoute>
                                </ProtectedRoute>
                            } />

                            {/* Ruta principal - Landing page para usuarios no autenticados */}
                            <Route path="/" element={
                                // isLoggedIn ? <MapaPage /> : <LandingPageAlianzaSocial />
                                isLoggedIn ? <LandingPageCapellanes /> : <LandingPageCapellanes />
                            } />
                            <Route path="/proyecto" element={<ProyectoPresentacion />} />
                            <Route path="/blockchain-integration" element={<BlockchainIntegration />} />
                            <Route path="/dashboard-analitico" element={<DashboardAnalitico />} />
                            <Route path="/home" element={<LandingPageCapellanes />} />
                            <Route path="/analisis-ejecutivo" element={<ExecutiveSummary />} />
                            <Route path="/analisis-venta" element={<VentaFase1Tracker />} />
                            <Route path="/comites-arquitectura" element={<CommitteeArchitecture />} />
                            <Route path="/nft-cooperativas" element={<NFTCooperativasPresentation />} />
                            <Route path="/comisiones" element={<AnalisisComisionesMUI />} />
                            <Route path="/desbloquear-niveles" element={<DesbloquearNivelesPage />} />
                            <Route path="/movimiento" element={<MovimientoPage />} />
                            <Route path="/admin/users" element={<Usuarios />} />
                            <Route path="/admin/usuario/:id" element={<EditarUsuario />} />
                            <Route path="/tarjeta/:id" element={<TarjetaPresentacion />} />
                            <Route path="/perfiles-onboarding" element={<PerfilesOnboarding />} />
                        </Routes>
                    </ContentWrapper>
                    <Loading />
                </Router>
            </ThemeProvider>
        </LocalizationProvider>
    );
};

export default App;